import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { emailApi, gmailApi } from "../lib/api";
import {
  Mail, Send, Loader2, Save, KeyRound, Server, AtSign, ChevronDown, ShieldCheck,
  ExternalLink, Zap, LogIn, Unlink, CheckCircle2, Info, AlertTriangle,
} from "lucide-react";

const inputClass =
  "w-full h-9 px-2.5 rounded-md border border-slate-200 bg-white text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-colors";

const SECURITY_OPTIONS = [
  { key: "starttls", label: "STARTTLS (port 587)" },
  { key: "ssl", label: "SSL/TLS (port 465)" },
  { key: "none", label: "None (unencrypted)" },
];

const INSTRUCTIONS = {
  gmail: "Clicking \u201cConnect Gmail\u201d takes you to Google's sign-in page to link your account. No password to type or store \u2014 once you approve access, emails send straight from your Gmail address.",
  smtp: "You'll need your mail server's host and port, plus either an app password (for Gmail SMTP) or your regular email password (for other providers). Gmail users: go to Google Account \u2192 Security \u2192 App Passwords to generate one.",
  resend: "You'll need a free account at resend.com, a sending domain verified there, and your API key from the Resend dashboard.",
};

const RESEND_STEPS = [
  { title: "Create a free Resend account", body: "Go to resend.com and sign up for a free account." },
  {
    title: "Add and verify your domain",
    body: "In Resend, go to Domains and click Add Domain. Enter your business domain (for example, yourbusiness.com). Resend will give you DNS records to add in your domain registrar (such as GoDaddy). Add those records, then click Verify. Wait a few minutes for the status to show Verified.",
  },
  { title: "Enable receiving", body: "On your domain page in Resend, make sure the Enable Receiving toggle is turned on." },
  { title: "Get your API key", body: "In Resend, go to API Keys in the left sidebar and create a new key. Copy it." },
  { title: "Enter your API key here", body: "Paste your Resend API key into the API Key field above and save your settings." },
  {
    title: "Set up the reply webhook",
    body: "In Resend, go to Webhooks in the left sidebar and click Add Webhook. Enter this URL: https://servicegurupro.com/api/inbox/webhooks/resend/email. Under Select events to listen, choose email.received. Save the webhook.",
  },
  {
    title: "Get your Signing Secret",
    body: "Click into the webhook you just created and copy its Signing Secret. Paste it into the Webhook Secret field above and save your settings — this lets us verify that inbound emails really came from Resend.",
  },
  {
    title: "Send a test email",
    body: "Use the Send test button above to confirm your email is sending correctly. Then reply to that test email from your personal inbox — the reply should appear in your Inbox within a few minutes.",
  },
];

/** Per-company outbound email — SMTP, Resend, or Gmail (OAuth). Admin-only (route is admin-gated).
 * `allowGmail` (default true) lets platform-console usages keep Gmail available while tenant
 * Company Settings disables it — companies may only use SMTP or Resend. */
export default function EmailSettingsCard({ allowGmail = true }) {
  const [s, setS] = useState(null);
  const [password, setPassword] = useState(""); // SMTP password — only sent when non-empty
  const [resendKey, setResendKey] = useState(""); // Resend key — only sent when non-empty
  const [webhookSecret, setWebhookSecret] = useState(""); // Resend webhook secret — only sent when non-empty
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null); // tab being viewed/edited — may differ from the saved active provider
  const [syncingInbox, setSyncingInbox] = useState(false);

  const reload = async () => {
    try {
      const cfg = await emailApi.getSettings();
      setS(cfg);
      setSelectedProvider(cfg.provider || "smtp");
      setDirty(false);
      setPassword("");
      setResendKey("");
      setWebhookSecret("");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load email settings");
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const mark = (patch) => {
    setS((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  // The tab currently being viewed/edited — NOT necessarily the saved active provider.
  const provider = selectedProvider || "smtp";
  const activeProvider = s?.provider || "smtp"; // the provider actually saved & in use

  const save = async () => {
    if (provider === "resend") {
      if (!s.fromEmail?.trim()) {
        toast.error("From email is required.");
        return;
      }
      if (!s.resendApiKeyConfigured && !resendKey) {
        toast.error("Enter your Resend API key.");
        return;
      }
    } else if (provider === "smtp") {
      if (!s.fromEmail?.trim()) {
        toast.error("From email is required.");
        return;
      }
      if (!s.smtpHost?.trim()) {
        toast.error("SMTP host is required.");
        return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        provider,
        fromName: s.fromName || "",
        fromEmail: s.fromEmail || "",
        replyTo: s.replyTo || "",
        inboundEmail: s.inboundEmail || "",
      };
      if (provider === "resend") {
        if (resendKey) payload.resendApiKey = resendKey;
        if (webhookSecret) payload.resendWebhookSecret = webhookSecret;
      } else if (provider === "smtp") {
        payload.smtpHost = s.smtpHost || "";
        payload.smtpPort = Number(s.smtpPort) || 587;
        payload.smtpSecurity = s.smtpSecurity || "starttls";
        payload.smtpUser = s.smtpUser || "";
        if (password) payload.smtpPassword = password;
        payload.imapHost = s.imapHost || "";
        payload.imapPort = Number(s.imapPort) || 993;
        payload.imapSecurity = s.imapSecurity || "ssl";
      }
      const updated = await emailApi.updateSettings(payload);
      setS(updated);
      setPassword("");
      setResendKey("");
      setWebhookSecret("");
      setDirty(false);
      toast.success("Email settings saved");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const connectGmail = () => {
    window.location.href = gmailApi.connectUrl();
  };

  const disconnectGmail = async () => {
    setDisconnecting(true);
    try {
      await gmailApi.disconnect();
      toast.success("Gmail disconnected");
      await reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to disconnect Gmail");
    } finally {
      setDisconnecting(false);
    }
  };

  const sendTest = async () => {
    if (!testTo.trim()) {
      toast.error("Enter an email address to send the test to.");
      return;
    }
    if (dirty) {
      toast.error("Save your settings before sending a test.");
      return;
    }
    setTesting(true);
    try {
      await emailApi.sendTest(testTo.trim());
      toast.success(`Test email sent to ${testTo.trim()}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test email failed");
    } finally {
      setTesting(false);
    }
  };

  const syncInboxNow = async () => {
    if (dirty) {
      toast.error("Save your settings before checking for replies.");
      return;
    }
    setSyncingInbox(true);
    try {
      const res = await emailApi.syncInboxNow();
      if (res.ok) {
        toast.success(res.recorded ? `${res.recorded} new repl${res.recorded === 1 ? "y" : "ies"} added to your Inbox` : "No new replies found");
      } else {
        toast.error(res.reason || "Inbound sync isn't configured yet.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to check for replies");
    } finally {
      setSyncingInbox(false);
    }
  };

  if (!s) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="email-card">
        <div className="text-center py-6 text-slate-400">
          <Loader2 className="h-4 w-4 inline animate-spin" />
        </div>
      </div>
    );
  }

  const statusLabel = s.configured
    ? activeProvider === "resend" ? "Resend connected" : activeProvider === "gmail" ? "Gmail connected" : "SMTP connected"
    : "Not configured";

  return (
    <div
      className={provider === "resend" ? "grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5 items-start lg:w-[calc(100%+21.25rem)]" : "max-w-3xl"}
    >
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="email-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="h-4 w-4 text-blue-600" />
        <h2 className="text-[14px] font-semibold text-slate-900">Email sending</h2>
        <span
          data-testid="email-connection-status"
          className={`ml-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded border ${
            s.configured
              ? "text-emerald-700 bg-emerald-50 border-emerald-200"
              : "text-amber-700 bg-amber-50 border-amber-200"
          }`}
        >
          {statusLabel}
        </span>
      </div>
      <p className="text-[12px] text-slate-500 mb-4">
        Send estimates, invoices, receipts, reminders and team invites from your own email. Choose a provider below. These
        credentials are private to your company.
      </p>

      {/* Provider toggle */}
      <div className="mb-3">
        <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Provider</label>
        <div className="inline-flex rounded-lg bg-slate-100 p-1 border border-slate-200" data-testid="email-provider-toggle">
          {[
            ["gmail", "Gmail", LogIn],
            ["smtp", "SMTP", Server],
            ["resend", "Resend", Zap],
          ].map(([key, label, Icon]) => {
            const disabled = key === "gmail" && !allowGmail;
            return (
              <button
                key={key}
                type="button"
                data-testid={`email-provider-${key}`}
                disabled={disabled}
                title={disabled ? "Gmail isn't available for company accounts — use SMTP or Resend." : undefined}
                onClick={() => { if (disabled) return; setSelectedProvider(key); setDirty(true); }}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                  disabled
                    ? "text-slate-300 cursor-not-allowed"
                    : provider === key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Inline plain-language instructions for the selected provider */}
      <div data-testid={`email-instructions-${provider}`} className="mb-4 rounded-lg border border-blue-100 bg-blue-50/60 px-3.5 py-2.5 flex items-start gap-2">
        <Info className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
        <p className="text-[12px] text-blue-800 leading-relaxed">{INSTRUCTIONS[provider]}</p>
      </div>

      {provider === "gmail" ? (
        /* Gmail OAuth connect */
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 mb-4" data-testid="email-gmail-fields">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">
            <LogIn className="h-3.5 w-3.5" /> Gmail account
          </div>
          {s.gmailConnected ? (
            <div className="flex items-center justify-between gap-3 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5">
              <div className="flex items-center gap-2 min-w-0">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold text-emerald-800 truncate" data-testid="email-gmail-connected-address">{s.gmailEmail}</div>
                  <div className="text-[11px] text-emerald-600">Connected — sending as this address</div>
                </div>
              </div>
              <Button
                type="button"
                onClick={disconnectGmail}
                disabled={disconnecting}
                data-testid="email-gmail-disconnect-btn"
                className="h-8 bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100 text-[12.5px] font-semibold shrink-0"
              >
                {disconnecting ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Unlink className="h-3.5 w-3.5 mr-1" />}
                Disconnect
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={connectGmail}
              data-testid="email-gmail-connect-btn"
              className="h-10 bg-[#0B1E3F] hover:bg-[#0B1E3F]/90 text-white text-[13px] font-semibold"
            >
              <LogIn className="h-4 w-4 mr-1.5" />
              Connect Gmail
            </Button>
          )}
          {s.gmailConnected && s.gmailScopeStale && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 flex items-start gap-2" data-testid="email-gmail-scope-stale">
              <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-[12.5px] font-semibold text-amber-900">Reconnect Gmail to enable Trash &amp; Restore</div>
                <p className="text-[11.5px] text-amber-800 leading-relaxed mt-0.5">
                  This connection was approved under our older, broader permission. Reconnect to switch it to the
                  narrower Gmail permission we now request — sending keeps working either way.
                </p>
                <Button
                  type="button"
                  onClick={connectGmail}
                  data-testid="email-gmail-reconnect-btn"
                  className="h-8 mt-2 bg-amber-600 hover:bg-amber-700 text-white text-[12px] font-semibold"
                >
                  <LogIn className="h-3.5 w-3.5 mr-1.5" /> Reconnect Gmail
                </Button>
              </div>
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-3 flex items-start gap-1.5">
            <Info className="h-3 w-3 mt-0.5 shrink-0" />
            Customer replies automatically thread back into your Inbox — no extra setup needed. Deleting an email in the
            Inbox moves that single message to your Gmail Trash, where it can be restored.
          </p>
        </div>
      ) : provider === "smtp" ? (
        /* SMTP server credentials */
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 mb-4" data-testid="email-smtp-fields">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">
            <Server className="h-3.5 w-3.5" /> SMTP server
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SMTP host">
              <input value={s.smtpHost || ""} onChange={(e) => mark({ smtpHost: e.target.value })} data-testid="email-smtp-host" placeholder="smtp.yourcompany.com" className={inputClass} />
            </Field>
            <Field label="Port">
              <input type="number" value={s.smtpPort ?? 587} onChange={(e) => mark({ smtpPort: e.target.value })} data-testid="email-smtp-port" placeholder="587" className={inputClass} />
            </Field>
            <Field label="Encryption">
              <div className="relative">
                <select value={s.smtpSecurity || "starttls"} onChange={(e) => mark({ smtpSecurity: e.target.value })} data-testid="email-smtp-security" className={`${inputClass} appearance-none pr-7 cursor-pointer`}>
                  {SECURITY_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
                <ChevronDown className="h-4 w-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </Field>
            <Field label="Username">
              <input value={s.smtpUser || ""} onChange={(e) => mark({ smtpUser: e.target.value })} data-testid="email-smtp-user" placeholder="you@yourcompany.com" className={inputClass} />
            </Field>
            <Field label="Password">
              <div className="relative">
                <KeyRound className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setDirty(true);
                  }}
                  data-testid="email-smtp-password"
                  placeholder={s.smtpPasswordConfigured ? "•••••••• (saved — leave blank to keep)" : "SMTP password / app password"}
                  className={`${inputClass} pl-8`}
                />
              </div>
            </Field>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-200">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1.5">
              <Mail className="h-3.5 w-3.5" /> Inbound replies (IMAP)
            </div>
            <p className="text-[11px] text-slate-500 mb-3">
              Add your mailbox's IMAP host to thread customer replies back into your Inbox automatically. Uses the same username/password above. Leave blank to disable.
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Field label="IMAP host">
                <input value={s.imapHost || ""} onChange={(e) => mark({ imapHost: e.target.value })} data-testid="email-imap-host" placeholder="imap.yourcompany.com" className={inputClass} />
              </Field>
              <Field label="Port">
                <input type="number" value={s.imapPort ?? 993} onChange={(e) => mark({ imapPort: e.target.value })} data-testid="email-imap-port" placeholder="993" className={inputClass} />
              </Field>
              <Field label="Encryption">
                <div className="relative">
                  <select value={s.imapSecurity || "ssl"} onChange={(e) => mark({ imapSecurity: e.target.value })} data-testid="email-imap-security" className={`${inputClass} appearance-none pr-7 cursor-pointer`}>
                    <option value="ssl">SSL/TLS (port 993)</option>
                    <option value="starttls">STARTTLS (port 143)</option>
                  </select>
                  <ChevronDown className="h-4 w-4 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </Field>
            </div>
          </div>
        </div>
      ) : (
        /* Resend API key */
        <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 mb-4" data-testid="email-resend-fields">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">
            <Zap className="h-3.5 w-3.5" /> Resend
          </div>
          <Field label="Resend API key">
            <div className="relative">
              <KeyRound className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="password"
                value={resendKey}
                onChange={(e) => {
                  setResendKey(e.target.value);
                  setDirty(true);
                }}
                data-testid="email-resend-key"
                placeholder={s.resendApiKeyConfigured ? "•••••••• (saved — leave blank to keep)" : "re_..."}
                className={`${inputClass} pl-8`}
              />
            </div>
          </Field>
          <Field label="Resend webhook secret">
            <div className="relative">
              <KeyRound className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="password"
                value={webhookSecret}
                onChange={(e) => {
                  setWebhookSecret(e.target.value);
                  setDirty(true);
                }}
                data-testid="email-resend-webhook-secret"
                placeholder={s.resendWebhookSecretConfigured ? "•••••••• (saved — leave blank to keep)" : "whsec_..."}
                className={`${inputClass} pl-8`}
              />
            </div>
          </Field>
          <p className="text-[11px] text-slate-500 mt-2">
            Your From email must be on a domain verified in your Resend account.{" "}
            <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">
              Get an API key <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        </div>
      )}

      {/* Sender identity (all providers) */}
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-4 mb-4">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-3">
          <AtSign className="h-3.5 w-3.5" /> Sender identity
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="From name">
            <input value={s.fromName || ""} onChange={(e) => mark({ fromName: e.target.value })} data-testid="email-from-name" placeholder="Acme Plumbing" className={inputClass} />
          </Field>
          <Field label="From email">
            {provider === "gmail" ? (
              <input value={s.gmailEmail || ""} disabled data-testid="email-from-email" className={`${inputClass} bg-slate-100 text-slate-500 cursor-not-allowed`} placeholder="Connect Gmail first" />
            ) : (
              <input value={s.fromEmail || ""} onChange={(e) => mark({ fromEmail: e.target.value })} data-testid="email-from-email" placeholder="billing@yourcompany.com" className={inputClass} />
            )}
          </Field>
          <Field label="Reply-to (optional)">
            <input value={s.inboundEmail || s.replyTo || ""} disabled={!!s.inboundEmail} onChange={(e) => mark({ replyTo: e.target.value })} data-testid="email-reply-to" placeholder="support@yourcompany.com" className={`${inputClass} disabled:opacity-60`} />
          </Field>
          <Field label="Unique inbound email address">
            <input type="email" value={s.inboundEmail || ""} onChange={(e) => mark({ inboundEmail: e.target.value })} data-testid="email-inbound-address" placeholder="Your configured receiving address" className={inputClass} />
          </Field>
        </div>
      </div>

      {/* Test email */}
      <div className="rounded-lg border border-slate-200 bg-white p-3.5 mb-1">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Send a test email</label>
            <div className="relative">
              <ShieldCheck className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="email-test-to" placeholder="you@yourcompany.com" className={`${inputClass} pl-8`} />
            </div>
          </div>
          <Button onClick={sendTest} disabled={testing || !s.configured} data-testid="email-test-btn" className="h-9 bg-[#0B1E3F] hover:bg-[#0B1E3F]/90 text-white text-[13px] font-semibold">
            {testing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
            Send test
          </Button>
        </div>
        {dirty && <p className="text-[11px] text-amber-600 mt-2">Save your changes before sending a test email.</p>}
      </div>

      {/* Manual inbound-reply check */}
      {(provider === "gmail" ? s.gmailConnected : provider === "smtp" ? !!s.imapHost : false) && (
        <div className="rounded-lg border border-slate-200 bg-white p-3.5 mb-1 mt-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-[12.5px] font-semibold text-slate-800">Check for new replies now</div>
            <div className="text-[11px] text-slate-500">Polls automatically every few minutes — trigger an immediate check any time.</div>
          </div>
          <Button
            type="button"
            onClick={syncInboxNow}
            disabled={syncingInbox || dirty}
            data-testid="email-sync-inbox-btn"
            className="h-9 bg-slate-800 hover:bg-slate-900 text-white text-[12.5px] font-semibold shrink-0"
          >
            {syncingInbox ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
            Check now
          </Button>
        </div>
      )}

      <div className="flex justify-end mt-4">
        <Button onClick={save} disabled={!dirty || saving} data-testid="email-save-btn" className="h-10 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-200 disabled:text-slate-500 text-white text-[13px] font-semibold">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
          Save email settings
        </Button>
      </div>
    </div>

    {provider === "resend" && (
      <div className="rounded-xl p-5" style={{ backgroundColor: "#0B1E3F" }} data-testid="email-resend-guide">
        <div className="flex items-center gap-2 mb-1">
          <Zap className="h-4 w-4 text-blue-300" />
          <h3 className="text-[13.5px] font-semibold text-white">How to connect Resend</h3>
        </div>
        <p className="text-[11.5px] text-blue-200/80 mb-4">Follow these steps once to connect Resend for sending and receiving email.</p>
        <ol className="space-y-4">
          {RESEND_STEPS.map((step, i) => (
            <li key={i} className="flex gap-3" data-testid={`email-resend-guide-step-${i + 1}`}>
              <span className="shrink-0 h-6 w-6 rounded-full bg-blue-500 text-white text-[12px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
              <div>
                <div className="text-[13px] font-semibold text-white">{step.title}</div>
                <p className="text-[12px] text-blue-100/85 leading-relaxed mt-0.5">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-semibold text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
